import { ButtonContainer, FormContainer } from './PunchupEditEventForm.styles';
import { IdParamsType, StateProps } from 'utils/types';
import { getFormDetails, handleInitialValues } from './utils';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { DateTime } from 'luxon';
import { EVENT_MODE_PUNCHUP } from 'utils/constants';
import { EditEventFormProps } from './types';
import { Form } from 'features/common/components/Form';
import { PunchupShowDashboardPage } from 'features/common/components/Punchup/ShowDashboardPage/PunchupShowDashboardPage';
import { PunchupButton } from 'features/common/components/Form/PunchupButton/PunchupButton';
import { PunchupDateTimePicker } from 'features/common/components/Form/PunchupDateTimePicker/PunchupDateTimePicker';
import { PunchupToggle } from 'features/common/components/Form/PunchupToggle/PunchupToggle';
import { SeatsChart, SeatsChartChangePreview } from 'services/Seats/types';
import { SeatsChartsList } from 'features/SeatsCharts';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { convertDateWithTimezoneToUTC } from 'utils/utils';
import { FlexContainer } from 'features/common/components/FlexContainer';
import { SeatsChartChangePreviewPanel } from './components/SeatsChartChangePreviewPanel';
import {
	buildChartChangeCsv,
	chartChangeCsvQueryKey,
	downloadChartChangeCsv,
} from './components/SeatsChartChangePreviewPanel/csv';
import {
	PunchupNotifyDateChangeDialog,
	buildDateChangePreview,
} from 'features/common/components/Punchup/composites/dialogs/PunchupNotifyDateChangeDialog';
import { useNotifyAttendeesOnDateChange } from 'features/common/hooks/useNotifyAttendeesOnDateChange';
import {
	ADJUST_SHOW_DATES_INFO,
	SHOW_SCHEDULE_HELP,
	persistedScheduleOf,
	useShowScheduleSync,
} from 'features/common/hooks/useShowScheduleSync';
import { postToast } from 'features/common/utils/postFlash';
import { useQueryClient } from '@tanstack/react-query';

export const PunchupEditEventForm = ({ event, editEvent }: EditEventFormProps) => {
	const { t } = useTranslation();
	const { goBack } = useHistory();
	const { id }: IdParamsType = useParams();
	const { state: { isFromRequiredFields } = { isFromRequiredFields: false } }: StateProps = useLocation();

	const [selectedChart, setSelectedChart] = useState<SeatsChart | null>(null);
	const [originalChartKey] = useState<string | undefined>(event.seats_chart_key);
	const [chartPreviewLoading, setChartPreviewLoading] = useState(false);
	const [chartPreviewError, setChartPreviewError] = useState(false);
	// Holds the submitted form values while the notify-attendees confirm dialog is open (date changed).
	const [pendingSave, setPendingSave] = useState<any>(null);
	const { notifyAsync } = useNotifyAttendeesOnDateChange();
	const [originalDates] = useState({
		startDate: event.days[0]?.start_date,
		endDate: event.days[0]?.end_date,
	});

	const formMethods = useForm({
		defaultValues: handleInitialValues(event),
		// Punchup shows persist as mode "normal", so `event.mode` can never select punchupSchema.
		resolver: getFormDetails(EVENT_MODE_PUNCHUP),
	});

	const { handleStartDateChange, revalidateScheduling } = useShowScheduleSync(formMethods, persistedScheduleOf(event));

	const startDate = formMethods.watch('dates.0.start_date');
	const doorTime = formMethods.watch('door_time');
	const changePassword = formMethods.watch('change_password');
	const wantToChangePassword = formMethods.watch('want_to_change_password');
	const hideEvent = formMethods.watch('hide_event');
	const hasPassword = event?.has_password;
	const seatsChartKey = formMethods.watch('seats_chart_key');
	const timeZone = formMethods.watch('timezone');

	const datesHaveChanged = startDate && convertDateWithTimezoneToUTC(startDate, timeZone) !== originalDates.startDate;

	useEffect(() => {
		if (hasPassword && !changePassword) {
			formMethods.setValue('remove_password', true);
		} else {
			formMethods.setValue('remove_password', false);
		}
	}, [changePassword, hasPassword, formMethods]);

	useEffect(() => {
		if (!hasPassword && changePassword) {
			formMethods.setValue('want_to_change_password', true);
		} else if (!hasPassword && !changePassword) {
			formMethods.setValue('want_to_change_password', false);
		}
	}, [changePassword, hasPassword, formMethods]);

	const queryClient = useQueryClient();

	const saveEvent = (values: any, notify: boolean, customMessage = '') => {
		// If the user is switching to a different seats chart, capture the
		// before/after-by-ticket CSV from the preview cache at the moment of
		// save so they have an offline record of the migration.
		if (values.seats_chart_key && originalChartKey && values.seats_chart_key !== originalChartKey) {
			const preview = queryClient.getQueryData<SeatsChartChangePreview>(
				chartChangeCsvQueryKey(id, values.seats_chart_key)
			);
			if (preview?.tickets?.length) {
				const filename = `chart-change-impact_event-${id}_${values.seats_chart_key}.csv`;
				downloadChartChangeCsv(filename, buildChartChangeCsv(preview.tickets));
			}
		}
		editEvent({
			values,
			id,
			// Notify runs only after the save persists (inside the mutation onSuccess). Its failure is
			// non-blocking — the show is already saved; surface a secondary toast and move on.
			onSaved: notify
				? () =>
						notifyAsync({ eventIds: [id], customMessage })
							.then(() => postToast('success', t('editEvent-notify-sent', 'Attendees notified of the date change')))
							.catch(() =>
								postToast('error', t('editEvent-notify-failed', "Show saved, but we couldn't notify attendees"))
							)
				: undefined,
		} as any);
	};

	const onEditEvent = (values: any) => {
		// Changing the show date affects everyone holding a ticket — offer to email them. Otherwise save directly.
		if (datesHaveChanged) {
			setPendingSave(values);
			return;
		}
		saveEvent(values, false);
	};

	const onClickCancel = () => {
		window.parent.postMessage({ action: 'event-edit-canceled', eventId: id }, '*');
		goBack();
	};

	const handleChartSelect = (chart: SeatsChart) => {
		if (selectedChart?.key === chart.key || seatsChartKey === chart.key) {
			setSelectedChart(null);
			formMethods.setValue('seats_chart_key', undefined);
		} else {
			setSelectedChart(chart);
			formMethods.setValue('seats_chart_key', chart.key);
		}
	};

	return (
		<PunchupShowDashboardPage pageName="Edit Show" onBack={onClickCancel}>
			<Form formMethods={formMethods} onSubmit={onEditEvent} width="100%">
				<FormContainer>
					<Form.PunchupInput
						id="name"
						name="name"
						label={t('editEvent-eventName')}
						isFromRequiredFields={isFromRequiredFields}
					/>

					<Form.PunchupInput id="venue" name="venue" label={t('editEvent-venue')} disabled />
					<Form.PunchupInput id="location" name="location" label={t('editEvent-eventLocation')} disabled />

					<Form.ShowDescription
						id="description"
						name="description"
						label={t('editEvent-event-description')}
						isPunchup
					/>

					<PunchupDateTimePicker
						id="startDate"
						name="dates[0].start_date"
						label={t('Show Start Date')}
						required
						help={SHOW_SCHEDULE_HELP.startDate}
						onChange={handleStartDateChange}
						minDate={new Date()}
					/>

					<PunchupDateTimePicker
						id="endDate"
						name="dates[0].end_date"
						label={t('Show End Date')}
						required
						help={SHOW_SCHEDULE_HELP.endDate}
						onChange={revalidateScheduling}
						minDate={startDate}
					/>
					<FlexContainer alignItems="center" marginBottom="16px">
						{/* The field name is the API payload (`adjust_end_date`); the label is what it does now. */}
						<PunchupToggle
							htmlFor="adjust_end_date"
							name="adjust_end_date"
							label={t('Adjust Show Dates')}
							info={ADJUST_SHOW_DATES_INFO}
							help={SHOW_SCHEDULE_HELP.adjustShowDates}
						/>
					</FlexContainer>
					{datesHaveChanged && (
						<FlexContainer alignItems="center" marginBottom="16px">
							<PunchupToggle
								htmlFor="adjust_ticket_dates"
								name="adjust_ticket_dates"
								label={t('Adjust Ticket Type Selling Dates')}
								info="Automatically shifts Ticket Types On and Off Sale Dates to match the new Show Dates"
							/>
						</FlexContainer>
					)}

					<PunchupDateTimePicker
						id="doorTime"
						name="door_time"
						label={t('Open Doors')}
						required
						help={SHOW_SCHEDULE_HELP.openDoors}
						onChange={revalidateScheduling}
						maxDate={startDate}
						minTime={new Date(0, 0, 0, 0, 0, 0)}
						maxTime={
							doorTime && DateTime.fromJSDate(startDate).hasSame(DateTime.fromJSDate(doorTime), 'day')
								? DateTime.fromJSDate(startDate).toJSDate()
								: new Date(0, 0, 0, 23, 59, 59)
						}
					/>

					<Form.PunchupTimezone eventTimezone={event.time_zone} />

					<PunchupToggle htmlFor="change_password" name="change_password" label={t('Protect event with password')} />

					{changePassword && hasPassword && (
						<PunchupToggle
							htmlFor="want_to_change_password"
							name="want_to_change_password"
							label={t('Change password')}
						/>
					)}

					{((changePassword && !hasPassword) || (changePassword && hasPassword && wantToChangePassword)) && (
						<>
							<Form.PunchupInput
								id="password"
								label={t('Password')}
								name="password"
								placeholder="Enter password"
								type="password"
								required
							/>
							<Form.PunchupInput
								id="confirm_password"
								label={t('Confirm Password')}
								name="confirm_password"
								placeholder="Confirm password"
								type="password"
								required
							/>
							<p style={{ fontSize: '12px', color: '#666', marginTop: '8px', marginBottom: '4px' }}>
								{t('Password must be at least 8 characters')}
							</p>
							<p style={{ fontSize: '12px', color: '#666', marginTop: '0px', marginBottom: '16px' }}>
								{t('Only users with this password will be able to view and purchase')}
							</p>
						</>
					)}

					<PunchupToggle htmlFor="hide_event" name="hide_event" label={t('Hide event until specific date')} />

					{hideEvent && (
						<>
							<PunchupDateTimePicker
								id="hidden_until"
								name="hidden_until"
								label={t('Hidden Until')}
								required
								minDate={new Date()}
							/>
							<p style={{ fontSize: '12px', color: '#666', marginTop: '-8px' }}>
								{t('Event will be hidden from public view until this date')}
							</p>
						</>
					)}

					{seatsChartKey && (
						<SeatsChartsList
							onChartSelect={handleChartSelect}
							selectedChartId={seatsChartKey || selectedChart?.key}
							title={t('Select Seat Chart')}
						/>
					)}

					{seatsChartKey && originalChartKey && seatsChartKey !== originalChartKey && (
						<SeatsChartChangePreviewPanel
							eventId={id}
							newChartKey={seatsChartKey}
							onLoadingChange={setChartPreviewLoading}
							onErrorChange={setChartPreviewError}
						/>
					)}
				</FormContainer>

				<ButtonContainer>
					<PunchupButton type="button" variant="secondary" onClick={onClickCancel} text="Cancel" />
					<PunchupButton
						type="submit"
						variant="primary"
						text="Save"
						// Save is never disabled by a validation error — pressing it names the problem instead.
						// It waits only on an unresolved seats-chart impact preview, whose effect is unknown.
						disabled={chartPreviewLoading || chartPreviewError}
					/>
				</ButtonContainer>
			</Form>

			{pendingSave &&
				createPortal(
					<PunchupNotifyDateChangeDialog
						preview={buildDateChangePreview({
							eventName: pendingSave.name,
							eventId: id,
							startDate: pendingSave.dates?.[0]?.start_date,
							endDate: pendingSave.dates?.[0]?.end_date,
							timeZone: pendingSave.timezone,
							venueName: pendingSave.venue,
							primaryColor: event.event_webpage_settings?.theme_colors,
							eventBanner: event.event_webpage_settings?.event_banner,
						})}
						onSkip={() => {
							const values = pendingSave;
							setPendingSave(null);
							saveEvent(values, false);
						}}
						onConfirm={(customMessage) => {
							const values = pendingSave;
							setPendingSave(null);
							saveEvent(values, true, customMessage);
						}}
					/>,
					document.body
				)}
		</PunchupShowDashboardPage>
	);
};
